import { isApiError } from '@/types';
import type { PlanCard, SubscriptionState, UsageSummary } from '@/types';

/**
 * Subscription/entitlement helpers — single source of truth for "can this
 * user use the app & trigger automation". The FE gate is UX only; the BE
 * enforces the same rule on the automation/generation endpoints
 * (see agent-artifacts/be-subscription-gate-contract.md).
 */

/** Statuses that grant full access. Everything else is treated as locked. */
const ENTITLED_STATUSES: ReadonlySet<SubscriptionState['status']> = new Set([
  'active',
  'trialing',
  'lifetime',
]);

/**
 * True when the subscription grants access. `none`, `canceled`, and any
 * unknown status are treated as locked. Lifetime purchasers are always
 * entitled regardless of the reported status.
 */
export function isSubscriptionActive(subscription: SubscriptionState | null): boolean {
  if (!subscription) return false;
  if (subscription.isLifetime) return true;
  return ENTITLED_STATUSES.has(subscription.status);
}

/**
 * Whether "Upgrade plan" leads anywhere: some purchasable plan costs more than
 * the current one. A Lifetime user on the top plan gets false, so a stale
 * limit error in an old chat no longer offers an upgrade they already have.
 * Unknown plan or empty catalog: true, so the way up is never hidden by
 * missing data.
 */
export function canUpgrade(subscription: SubscriptionState | null, catalog: PlanCard[]): boolean {
  if (!isSubscriptionActive(subscription)) return true;
  const current = catalog.find(p => p.id === subscription?.planId);
  if (!current) return true;
  return catalog.some(p => p.purchasable && p.price.amount > current.price.amount);
}

/**
 * Posts the user can actually reach this period. The usage limit comes first:
 * a one-platform trial's catalog total sums caps it can never combine.
 */
export function postsTotal(usage: UsageSummary | null, plan: PlanCard | undefined): number | null {
  return usage?.posts.limit ?? plan?.totalMonthlyPosts ?? null;
}

/** Plan-status line for billing/profile. A trial ends, it never renews. */
export function renewalLabel(subscription: SubscriptionState): string {
  if (subscription.isLifetime) return 'Lifetime access';
  const date = subscription.currentPeriod.resetsOn;
  if (subscription.cancelAtPeriodEnd) return `Cancels on ${date}`;
  if (subscription.status === 'none') return 'No active subscription';
  if (subscription.status === 'trialing') return `Trial expires ${date}`;
  return `Renews ${date}`;
}

/**
 * Per-feature 402 codes thrown by the BE `EntitlementsService.deny()` — the
 * user HAS an active plan but hit a quota cap or a feature their tier lacks.
 * These drive the `UpgradeModal` (nudge to a higher tier). Distinct from
 * `NO_ACTIVE_PLAN` (no plan at all), which drives the full-page Paywall.
 */
const PLAN_LIMIT_CODES: ReadonlySet<string> = new Set([
  'PLAN_LIMIT_REACHED',
  'FEATURE_NOT_IN_PLAN',
]);

/**
 * BE code returned by the route-level `ActivePlanGuard` (M4) on a 402 when an
 * authenticated user has no active plan. Unlike the per-feature codes above,
 * this fires on *every* gated endpoint, so it means "gate the whole app",
 * not "nudge to a higher tier".
 */
const NO_ACTIVE_PLAN_CODE = 'NO_ACTIVE_PLAN';

/**
 * True when an error is a 402 plan-limit / per-feature entitlement block.
 * These are handled globally by the `UpgradeListener` (which opens the
 * `UpgradeModal` off the `billing:limit-reached` event), so limit-prone
 * callers should use this to SKIP their own `toast.error(...)` and avoid
 * showing a duplicate — and misleading "Failed to…" — toast alongside the
 * upgrade modal. Does NOT match `NO_ACTIVE_PLAN` — use
 * `isSubscriptionRequiredError` for that.
 */
export function isPlanLimitError(err: unknown): boolean {
  return isApiError(err) && PLAN_LIMIT_CODES.has(err.error.code);
}

/**
 * True when an error is the BE `402 NO_ACTIVE_PLAN` block (no active plan).
 * Handled globally by `SubscriptionGate` (which surfaces the full-page
 * `Paywall` off the `billing:subscription-required` event), so callers should
 * use this — alongside `isPlanLimitError` — to SKIP their own error toast.
 */
export function isSubscriptionRequiredError(err: unknown): boolean {
  return isApiError(err) && err.error.code === NO_ACTIVE_PLAN_CODE;
}

/**
 * True for any billing 402 that is surfaced GLOBALLY — the per-feature
 * UpgradeModal (`isPlanLimitError`) or the full-page Paywall
 * (`isSubscriptionRequiredError`). Use at call sites that just need to SKIP
 * their own `toast.error(...)` so it doesn't double up with the global UI.
 */
export function isBillingBlockError(err: unknown): boolean {
  return isPlanLimitError(err) || isSubscriptionRequiredError(err);
}
